import * as React from "react"
import { BillingInterval, MachinePricingTier } from "@modules/graphql/types"
import { planInformation as planInformationText } from "@modules/locales/default.js"
import { MdEdit, MdClose } from "react-icons/md"
import { DiDatabase } from "react-icons/di"
import { FormattedMessage, commonMessageTags } from "@modules/locales"
import {
  IconProps,
  FreePlanIcon,
  IndividualPlanIcon,
  TeamPlanIcon,
  EnterprisePlanIcon,
  HostingFreePlanIcon,
  HostingStandardPlanIcon,
  HostingPerformancePlanIcon,
  HostingEnterprisePlanIcon,
  DefaultSpeedIcon,
  FastSpeedIcon,
  FasterSpeedIcon,
  FastestSpeedIcon,
  OneUserIcon,
  ManyUsersIcon,
  UnlimitedUsersIcon,
  FreeBandwidthIcon,
  FreeRequestsIcon,
  StandardBandwidthIcon,
  StandardRequestsIcon,
  ProBandwidthIcon,
  ProRequestsIcon,
  getTheme,
  BuildsIcon,
} from "gatsby-interface"

const { colors } = getTheme()

export type PlanInfo = {
  planId: string | null
  planName: string | null
  billingInterval: BillingInterval
  buildsTier?: MachinePricingTier | null
  hostingTier?: MachinePricingTier | null
}

export enum SupportedTier {
  Free = `FREE`,
  Standard = `STANDARD`,
  Performance = `PERFORMANCE`,
  Enterprise = `ENTERPRISE`,
}

export type SupportedPricingTier =
  | MachinePricingTier.Free
  | MachinePricingTier.Standard
  | MachinePricingTier.Performance
  | MachinePricingTier.Enterprise

export type RichFeature = {
  icon?: (props: IconProps) => JSX.Element
  label: React.ReactNode
  negative?: boolean
}

export type TierType = `builds` | `hosting`

function IncrementalBuildLable() {
  return <span>Incremental Cloud Builds</span>
}

export type TierInformation = {
  key: SupportedTier
  pricingTier: SupportedPricingTier
  name: string
  color: string
  monthlyAmount?: Record<BillingInterval, number | null>
  buildsIcon?: (props: IconProps) => JSX.Element
  buildsMonthlyAmount?: Record<BillingInterval, number | null>
  buildsDescription?: string
  buildsFeaturesIntro?: string
  buildsFeatures?: RichFeature[]
  hostingIcon?: (props: IconProps) => JSX.Element
  hostingDescription?: string
  hostingFeaturesIntro?: string
  hostingFeatures?: RichFeature[]
  hostingMonthlyAmount?: Record<BillingInterval, number | null>
  cta?: string
}

export const supportedTiers: Omit<
  Record<MachinePricingTier, TierInformation>,
  "OPTIMIZED"
> = {
  FREE: {
    key: SupportedTier.Free,
    pricingTier: MachinePricingTier.Free,
    name: planInformationText.morphemes.freePlanName,
    color: colors.blue[80],
    buildsIcon: FreePlanIcon,
    buildsDescription: `For non-commercial projects and MVPs`,
    buildsFeatures: [
      {
        icon: OneUserIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> User"
            values={{
              value: "1",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: DefaultSpeedIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="{value} Build Speed"
            values={{
              value: "Default",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: MdClose,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Cloud Builds"
            values={{
              value: "Incremental",
            }}
            tags={commonMessageTags}
          />
        ),
        negative: true,
      },
      {
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Deploy Previews"
            values={{
              value: "Branch",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> CMS Previews"
            values={{
              value: "Instant",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: BuildsIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Builds"
            values={{
              value: "Limited",
            }}
            tags={commonMessageTags}
          />
        ),
      },
    ],
    hostingIcon: HostingFreePlanIcon,
    hostingDescription: `For non-commercial projects and MVPs`,
    hostingFeatures: [
      {
        icon: FreeBandwidthIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Bandwidth"
            values={{
              value: "100GB",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: FreeRequestsIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Requests"
            values={{
              value: "5M",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Website"
            values={{
              value: "1",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: `Global Edge Network`,
      },
      {
        label: `Free SSL Certificates`,
      },
      {
        label: `Custom Domains`,
      },
      {
        icon: MdClose,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Rollbacks"
            values={{
              value: "Instant",
            }}
            tags={commonMessageTags}
          />
        ),
        negative:true,
      },
    ],
    buildsMonthlyAmount: {
      MONTHLY: 0,
      ANNUAL: 0,
    },
    hostingMonthlyAmount: {
      MONTHLY: 0,
      ANNUAL: 0,
    },
  },

  STANDARD: {
    key: SupportedTier.Standard,
    pricingTier: MachinePricingTier.Standard,
    name: planInformationText.morphemes.standardPlanName,
    color: colors.red[80],
    buildsIcon: IndividualPlanIcon,
    buildsDescription: `For Gatsby projects that are growing`,
    buildsFeatures: [
      {
        icon: ManyUsersIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="Up to <strong>{value}</strong> Users"
            values={{
              value: "2",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: FastSpeedIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Build Speed"
            values={{
              value: "Default",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: <IncrementalBuildLable />,
      },
      {
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Deploy Previews"
            values={{
              value: "Branch",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> CMS Previews"
            values={{
              value: "Instant",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Builds"
            values={{
              value: "Unlimited",
            }}
            tags={commonMessageTags}
          />
        ),
      },
    ],
    hostingIcon: HostingStandardPlanIcon,
    hostingDescription: `For Gatsby projects that are growing`,
    hostingFeatures: [
      {
        icon: StandardBandwidthIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Bandwidth"
            values={{
              value: "300GB",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: StandardRequestsIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Requests"
            values={{
              value: "30M",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Websites"
            values={{
              value: "10",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: `Global Edge Network`,
      },
      {
        label: `Free SSL Certificates`,
      },
      {
        label: `Custom Domains`,
      },
      {
        icon: MdClose,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Rollbacks"
            values={{
              value: "Instant",
            }}
            tags={commonMessageTags}
          />
        ),
        negative:true,
      },
    ],
    buildsMonthlyAmount: {
      MONTHLY: 2000,
      ANNUAL: 20400 / 12,
    },
    hostingMonthlyAmount: {
      MONTHLY: 2000,
      ANNUAL: 20400 / 12,
    },
  },

  PERFORMANCE: {
    key: SupportedTier.Performance,
    pricingTier: MachinePricingTier.Performance,
    name: planInformationText.morphemes.performancePlanName,
    color: colors.magenta[60],
    buildsIcon: TeamPlanIcon,
    buildsDescription: `For large teams dependent on Gatsby`,
    buildsFeatures: [
      {
        icon: ManyUsersIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="Up to <strong>{value}</strong> Users"
            values={{
              value: "10",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: FasterSpeedIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Build Speed"
            values={{
              value: "Premium",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: <IncrementalBuildLable />,
      },
      {
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Deploy Previews"
            values={{
              value: "Branch",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> CMS Previews"
            values={{
              value: "Instant",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Builds"
            values={{
              value: "Unlimited",
            }}
            tags={commonMessageTags}
          />
        ),
      },
    ],
    hostingIcon: HostingPerformancePlanIcon,
    hostingDescription: `For large teams dependent on Gatsby`,
    hostingFeatures: [
      {
        icon: ProBandwidthIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Bandwidth"
            values={{
              value: "1TB",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: ProRequestsIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Requests"
            values={{
              value: "80M",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Websites"
            values={{
              value: "25",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: `Global Edge Network`,
      },
      {
        label: `Free SSL Certificates`,
      },
      {
        label: `Custom Domains`,
      },
      {
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Rollbacks"
            values={{
              value: "Instant",
            }}
            tags={commonMessageTags}
          />
        ),
      },
    ],
    buildsMonthlyAmount: {
      MONTHLY: 10000,
      ANNUAL: 102000 / 12,
    },
    hostingMonthlyAmount: {
      MONTHLY: 10000,
      ANNUAL: 102000 / 12,
    },
  },
  ENTERPRISE: {
    key: SupportedTier.Enterprise,
    pricingTier: MachinePricingTier.Enterprise,
    name: planInformationText.morphemes.enterprisePlanName,
    color: colors.purple[60],
    buildsIcon: EnterprisePlanIcon,
    buildsDescription: `All the Performance plan features plus:`,
    buildsFeatures: [
      {
        icon: UnlimitedUsersIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Users"
            values={{
              value: "Unlimited",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: FastestSpeedIcon,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> CPU & RAM"
            values={{
              value: "Dedicated",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong>"
            values={{
              value: "SAML & SSO",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Audits"
            values={{
              value: "Gatsby Concierge",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Support"
            values={{
              value: "Gatsby Concierge",
            }}
            tags={commonMessageTags}
          />
        ),
      },
    ],
    hostingIcon: HostingEnterprisePlanIcon,
    hostingDescription: `All the Performance plan features plus:`,
    hostingFeatures: [
      {
        icon: DiDatabase,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Bandwidth"
            values={{
              value: "Custom",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        icon: MdEdit,
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Requests"
            values={{
              value: "Custom",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Audits"
            values={{
              value: "Gatsby Concierge",
            }}
            tags={commonMessageTags}
          />
        ),
      },
      {
        label: (
          <FormattedMessage<"value", "strong">
            message="<strong>{value}</strong> Support"
            values={{
              value: "Gatsby Concierge",
            }}
            tags={commonMessageTags}
          />
        ),
      },
    ],
    buildsMonthlyAmount: {
      MONTHLY: null,
      ANNUAL: null,
    },
    hostingMonthlyAmount: {
      MONTHLY: null,
      ANNUAL: null,
    },
    cta: planInformationText.actions.pickTier4,
  },
}
